import { addDays, parseBackendDate, roundUpToStep, toBackendDateTime, toLocalInput } from '../../lib/dates';

/**
 * Business limits for the Basics step. Sources:
 *  - MIN_AGE 14, MAX_FIELD_DAYS 30, recommended 200 completes: BRD "RULES".
 *  - MAX_AGE 62, LOI 1-35, completes 1-99999, IR 5-100: limits already enforced by the legacy UI.
 * Per-country IR/LOI rules are NOT known yet; when they exist they should replace LIMITS per country.
 */
export const LIMITS = {
  MIN_AGE: 14,
  MAX_AGE: 62,
  LOI: { min: 1, max: 35 },
  COMPLETES: { min: 1, max: 99999, recommended: 200 },
  IR: { min: 5, max: 100 },
  MAX_FIELD_DAYS: 30,
};

export const GENDERS = ['Both', 'Male', 'Female'];

/** A new group starts at least this far ahead of "now", on a quarter hour, so it is still in the future while the form is filled in. */
export const START_LEAD_MINUTES = 30;
export const defaultStart = (now = new Date()) => roundUpToStep(new Date(now.getTime() + START_LEAD_MINUTES * 60 * 1000), 15);

/** Defaults mirror the legacy form, except that the start is a sensible future time. Dates are datetime-local strings. */
export const defaultBasics = (now = new Date()) => ({
  startDate: toLocalInput(defaultStart(now)),
  endDate: toLocalInput(addDays(defaultStart(now), 2)),
  country_id: '',
  ageMinLimit: '19',
  ageMaxLimit: '40',
  gender: 'Both',
  estimatedLenghthOfInterview: '35', // sic: field name is spelled this way in the backend model
  noOfWantedCompletes: '100',
  estimatedIncidenceRate: '5',
});

/** Saved BasicSettings (backend) -> form values. */
export const basicsFromSaved = (saved) => ({
  startDate: parseBackendDate(saved.startDate) ? toLocalInput(parseBackendDate(saved.startDate)) : '',
  endDate: parseBackendDate(saved.endDate) ? toLocalInput(parseBackendDate(saved.endDate)) : '',
  country_id: saved.country_id != null ? String(saved.country_id) : '',
  ageMinLimit: String(saved.ageMinLimit ?? ''),
  ageMaxLimit: String(saved.ageMaxLimit ?? ''),
  gender: saved.gender || 'Both',
  estimatedLenghthOfInterview: String(saved.estimatedLenghthOfInterview ?? ''),
  noOfWantedCompletes: String(saved.noOfWantedCompletes ?? ''),
  estimatedIncidenceRate: String(saved.estimatedIncidenceRate ?? ''),
});

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Move the start and carry the end with it, so the length of the field period is kept (like a calendar app).
 * If the old period cannot be kept (no valid dates, or over the 30-day cap) the end becomes start + 2 days.
 */
export function moveStart(values, newStart) {
  const start = new Date(newStart);
  if (Number.isNaN(start.getTime())) return { ...values, startDate: newStart };
  const oldStart = new Date(values.startDate);
  const oldEnd = new Date(values.endDate);
  let length = oldEnd.getTime() - oldStart.getTime();
  if (!Number.isFinite(length) || length <= 0 || length > LIMITS.MAX_FIELD_DAYS * DAY_MS) length = 2 * DAY_MS;
  return { ...values, startDate: newStart, endDate: toLocalInput(new Date(start.getTime() + length)) };
}

/**
 * A start the user never touched (the pre-filled default) can drift into the past while the form is open.
 * Before saving, move it to the next sensible future time, keeping the field period. A start the user chose
 * is never changed here: it is validated as typed.
 */
export function rollStaleStart(values, now = new Date()) {
  const start = new Date(values.startDate);
  if (Number.isNaN(start.getTime()) || start.getTime() >= now.getTime()) return values;
  return moveStart(values, toLocalInput(defaultStart(now)));
}

const isInt = (s) => /^\d+$/.test(String(s).trim());

/**
 * Returns { field: message } (empty when valid).
 * `startChanged` is false when editing an existing group whose start date is already in the past,
 * so an old start date does not block saving unrelated edits.
 */
export function validateBasics(v, { now = new Date(), startChanged = true } = {}) {
  const e = {};
  if (!v.country_id) e.country_id = 'Select a country.';

  const min = isInt(v.ageMinLimit) ? Number(v.ageMinLimit) : null;
  const max = isInt(v.ageMaxLimit) ? Number(v.ageMaxLimit) : null;
  if (min === null || min < LIMITS.MIN_AGE || min > LIMITS.MAX_AGE) e.ageMinLimit = `Minimum age must be ${LIMITS.MIN_AGE}-${LIMITS.MAX_AGE}.`;
  if (max === null || max > LIMITS.MAX_AGE || max < LIMITS.MIN_AGE) e.ageMaxLimit = `Maximum age must be ${LIMITS.MIN_AGE}-${LIMITS.MAX_AGE}.`;
  else if (min !== null && max < min) e.ageMaxLimit = 'Maximum age cannot be below the minimum age.';

  const range = (key, label, { min: lo, max: hi }) => {
    if (!isInt(v[key]) || Number(v[key]) < lo || Number(v[key]) > hi) e[key] = `${label} must be a whole number between ${lo} and ${hi}.`;
  };
  range('estimatedLenghthOfInterview', 'Length of interview', LIMITS.LOI);
  range('noOfWantedCompletes', 'Wanted completes', LIMITS.COMPLETES);
  range('estimatedIncidenceRate', 'Incidence rate', LIMITS.IR);

  const start = new Date(v.startDate);
  const end = new Date(v.endDate);
  if (!v.startDate || Number.isNaN(start.getTime())) e.startDate = 'Choose a start date and time.';
  else if (startChanged && start.getTime() < now.getTime() - 60 * 1000) e.startDate = 'Start date cannot be in the past.';
  if (!v.endDate || Number.isNaN(end.getTime())) e.endDate = 'Choose an end date and time.';
  else if (!e.startDate) {
    if (end.getTime() <= start.getTime()) e.endDate = 'End must be after the start.';
    else if (end.getTime() > addDays(start, LIMITS.MAX_FIELD_DAYS).getTime()) e.endDate = `The field period is at most ${LIMITS.MAX_FIELD_DAYS} days.`;
  }
  return e;
}

/** Non-blocking advice (BRD recommends >= 200 completes for statistical reliability). */
export const basicsWarnings = (v) =>
  isInt(v.noOfWantedCompletes) && Number(v.noOfWantedCompletes) < LIMITS.COMPLETES.recommended
    ? { noOfWantedCompletes: `${LIMITS.COMPLETES.recommended}+ completes are recommended for reliable results.` }
    : {};

/**
 * Form values -> BasicSettings body, exactly as documented in the backend handover (no `id`: each POST
 * "replaces that part" of the target group, so an edit simply posts the full new values).
 */
export const basicsToPayload = (v) => ({
  startDate: toBackendDateTime(v.startDate),
  endDate: toBackendDateTime(v.endDate),
  country_id: String(v.country_id),
  ageMinLimit: Number(v.ageMinLimit),
  ageMaxLimit: Number(v.ageMaxLimit),
  gender: v.gender,
  estimatedLenghthOfInterview: Number(v.estimatedLenghthOfInterview),
  noOfWantedCompletes: Number(v.noOfWantedCompletes),
  estimatedIncidenceRate: Number(v.estimatedIncidenceRate),
});
